import { Address, RequestAdditionalConfig } from '@shared/types';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { DeliveryOption } from './DeliveryOption';
import { LocationLabel } from './LocationLabel';
import { LocationSelect } from './LocationSelect';
import styles from './styles.module.css';
import { RadioGroup } from '../../../../../RadioGroup';
import {
    CameraHomeSolidIcon,
    HousePersonLeaveSolidIcon,
    HousePersonReturnSolidIcon,
} from '../../../../../Icons';
import { AddressInput } from '@cademy-ui/form';
import { PublicOnRequestCourse } from '@cademy-package/sdk-component-types';

export type DeliverySelectorProps = {
    id?: string;
    course: PublicOnRequestCourse;
    educatorName: string;
    value: Required<RequestAdditionalConfig>['on_request_delivery'];
    onChange: (newDelivery: Required<RequestAdditionalConfig>['on_request_delivery']) => unknown;
};

type OnlineOption = { method: 'online' };
type EducatorTravelsOption = { method: 'educator_travels' };
type StudentTravelsOption = { method: 'student_travels'; locations: Array<string> };
type Option = OnlineOption | EducatorTravelsOption | StudentTravelsOption;
export const DeliverySelector: FunctionComponent<DeliverySelectorProps> = ({
    id = '',
    course,
    educatorName,
    value,
    onChange,
}) => {
    const options = useMemo((): Array<Option> => {
        return [
            ...(course.available_for_online_delivery ? [{ method: 'online' } as const] : []),
            ...(course.available_for_in_person_delivery && course.educator_travel_settings.enabled
                ? [{ method: 'educator_travels' } as const]
                : []),
            ...(course.available_for_in_person_delivery && course.student_travel_settings.enabled
                ? [
                      {
                          method: 'student_travels',
                          locations: course.student_travel_settings.locations,
                      } as const,
                  ]
                : []),
        ];
    }, [course]);
    const selectedTypeOption = useMemo((): Option => {
        const defaultOption = options[0];
        switch (value?.type) {
            case 'Online': {
                return options.find((option) => option.method === 'online') || defaultOption;
            }
            case 'Educator travels': {
                return (
                    options.find((option) => option.method === 'educator_travels') || defaultOption
                );
            }
            case 'Student travels': {
                return (
                    options.find((option) => option.method === 'student_travels') || defaultOption
                );
            }
        }
        return defaultOption;
    }, [value, options]);

    const setSelectedOption = useCallback(
        (newOption: Option) => {
            switch (newOption.method) {
                case 'online': {
                    return onChange({ type: 'Online' });
                }
                case 'educator_travels': {
                    return onChange({ type: 'Educator travels', address: {} });
                }
                case 'student_travels': {
                    return onChange({
                        type: 'Student travels',
                        location_ref: course.student_travel_settings.locations[0],
                    });
                }
            }
        },
        [onChange, course]
    );

    return (
        <div className={styles.container}>
            <h2 id={`${id}_header`}>Delivery</h2>
            <RadioGroup
                aria-labelledby={`${id}_header`}
                options={options}
                value={selectedTypeOption}
                onChange={setSelectedOption}
            >
                {(option, isSelected, isFocussed) => {
                    switch (option.method) {
                        case 'online':
                            return (
                                <DeliveryOption
                                    icon={<CameraHomeSolidIcon />}
                                    label={'Online'}
                                    checked={isSelected}
                                    focussed={isFocussed}
                                />
                            );
                        case 'student_travels':
                            const selectedLocation =
                                value?.type === 'Student travels'
                                    ? value.location_ref
                                    : option.locations[0];
                            const setSelectedLocation = (newLocation: string) => {
                                return onChange({
                                    type: 'Student travels',
                                    location_ref: newLocation,
                                });
                            };
                            return (
                                <DeliveryOption
                                    icon={<HousePersonLeaveSolidIcon />}
                                    label={`In-person • I will travel to ${educatorName}`}
                                    checked={isSelected}
                                    focussed={isFocussed}
                                >
                                    {option.locations.length > 1 ? (
                                        <>
                                            <label
                                                htmlFor={`${id}_location-select`}
                                                className={styles.select_location_label}
                                            >
                                                Select a location
                                            </label>
                                            <LocationSelect
                                                id={`${id}_location-select`}
                                                value={selectedLocation}
                                                onChange={setSelectedLocation}
                                                locationIds={option.locations}
                                            />
                                        </>
                                    ) : (
                                        <LocationLabel locationId={option.locations[0]} />
                                    )}
                                </DeliveryOption>
                            );

                        case 'educator_travels':
                            const selectedAddress =
                                value?.type === 'Educator travels' ? value.address : {};
                            const setSelectedAddress = (newAddress: Address | null) => {
                                onChange({
                                    type: 'Educator travels',
                                    address: newAddress || {},
                                });
                            };
                            return (
                                <DeliveryOption
                                    icon={<HousePersonReturnSolidIcon />}
                                    label={`In-person • I will host ${educatorName}`}
                                    checked={isSelected}
                                    focussed={isFocussed}
                                >
                                    <AddressInput
                                        label="Enter your address"
                                        name="address"
                                        value={selectedAddress}
                                        onChange={setSelectedAddress}
                                    />
                                </DeliveryOption>
                            );
                    }
                    return null;
                }}
            </RadioGroup>
        </div>
    );
};
